import React from 'react';

/**
 * Runs `reset` whenever `key` changes — and only then.
 *
 * Nine form hooks had written this by hand as a `useEffect` whose body touched
 * a dozen values and whose dependency list named one, silenced with
 * `eslint-disable-next-line react-hooks/exhaustive-deps`. The intent was right:
 * a modal re-seeds its fields when it opens or when the record it edits
 * changes, and re-running because one of those fields moved would fight the
 * user's typing. What was wrong was saying so by turning the rule off, which
 * also turns it off for every *other* mistake in the same effect — a dependency
 * that genuinely should have been listed no longer gets flagged.
 *
 * Here the dependency list is complete and true: the effect depends on `key`
 * and on a ref, and a ref is stable. `reset` is read through that ref at the
 * moment the effect fires, so it always sees the current render's closure —
 * the same freshness the disabled version got, without the blanket silence.
 *
 * The ref is written in `useInsertionEffect`, which runs before every other
 * effect, rather than during render. Writing a ref during render is what React
 * warns about under concurrent rendering: a render can be started, abandoned
 * and restarted, and the write would survive the render that never committed.
 *
 * ```ts
 * useResetOnKeyChange(isOpen, () => {
 *     setName(initial.name);
 *     setAddress(initial.address);
 * });
 * ```
 */
export const useResetOnKeyChange = <K>(key: K, reset: () => void): void => {
    const latest = React.useRef(reset);

    React.useInsertionEffect(() => {
        latest.current = reset;
    }, [reset]);

    React.useEffect(() => {
        latest.current();
        // `latest` is a ref: stable for the component's lifetime. `key` is the
        // whole trigger, which is the point of the hook.
    }, [key]);
};
